import { FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Paper,
  Select,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import {
  IconBrandWhatsapp,
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconInfoCircle,
  IconLink,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import SCModal from "@/PageComponents/Modal/SCModal";
import employeeService from "@/services/employee/employee-service";
import useEmployeeAvailability, {
  AvailabilityField,
} from "@/hooks/useEmployeeAvailability";
import Fetch from "@/utils/Fetch";
import Helper from "@/utils/helper";
import Constants from "@/utils/constants";
import Storage from "@/utils/storage";
import * as Enums from "@/utils/enums";

interface InviteRow {
  key: string;
  email: string;
  mobile: string;
  fullName: string;
  role: string;
  errors: any;
}

interface InviteResult {
  email: string;
  mobile: string;
  firstName: string;
  lastName: string;
  role: string;
  sent: boolean;
  inviteLink?: string;
  whatsAppUrl?: string;
  error?: string;
}

const newRow = (): InviteRow => ({
  key: crypto.randomUUID(),
  email: "",
  mobile: "",
  fullName: "",
  role: "",
  errors: {},
});

const emptyRows = () => [newRow()];

const pageSize = 10;

const splitFullName = (fullName: string) => {
  const trimmed = fullName.trim();
  const firstSpace = trimmed.indexOf(" ");

  if (firstSpace === -1) return { firstName: trimmed, lastName: "" };

  return {
    firstName: trimmed.slice(0, firstSpace),
    lastName: trimmed.slice(firstSpace + 1).trim(),
  };
};

const isOtherRole = (option: { value: string; label: string }) =>
  option.value.trim().toLowerCase() === "other" ||
  option.label.trim().toLowerCase() === "other";

const hasRowErrors = (row: InviteRow) =>
  Object.values(row.errors || {}).some(Boolean);

const peopleLabel = (count: number) =>
  `${count} ${count === 1 ? "person" : "people"}`;

const headerCopy = {
  settings: {
    title: "Invite employees",
    subtitle: "We'll email and SMS each person a link to join.",
  },
  onboarding: {
    title: "Add your team",
    subtitle:
      "Start with the people who'll set up and test ServCraft with you, like your office admin or project champion. We'll email and SMS each person a link to join.",
  },
};

const isBlankRow = (row: InviteRow) =>
  !row.email.trim() &&
  !row.mobile.trim() &&
  !row.fullName.trim() &&
  !row.role.trim();

const availabilityKey = (rowKey: string, field: AvailabilityField) =>
  `${rowKey}:${field}`;

const availabilityFields: { [key: string]: AvailabilityField } = {
  email: "EmailAddress",
  mobile: "MobileNumber",
};

const validationItemFor = (field: AvailabilityField, value: string) =>
  field === "EmailAddress"
    ? {
        key: field,
        value,
        required: true,
        type: Enums.ControlType.Email,
        rejectServCraft: true,
      }
    : {
        key: field,
        value,
        required: true,
        type: Enums.ControlType.ContactNumber,
      };

const requiredMark = (
  <span style={{ color: "var(--mantine-color-red-6)" }}> *</span>
);

interface InviteEmployeesModalProps {
  open: boolean;
  onClose: () => void;
  onInvited?: () => void;
  origin?: "onboarding" | "settings";
}

const InviteEmployeesModal: FC<InviteEmployeesModalProps> = ({
  open,
  onClose,
  onInvited,
  origin = "settings",
}) => {
  const [rows, setRows] = useState<InviteRow[]>(emptyRows);
  const [roleOptions, setRoleOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<InviteResult[] | null>(null);
  const [anySent, setAnySent] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [page, setPage] = useState(0);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [availabilityErrors, setAvailabilityErrors] = useState<{
    [token: string]: string;
  }>({});

  const { queueCheck, cancelCheck, cancelAll } = useEmployeeAvailability(
    (token, _field, message) => {
      setAvailabilityErrors((previous) => {
        if (!message) {
          if (!previous[token]) return previous;

          const next = { ...previous };
          delete next[token];
          return next;
        }

        return { ...previous, [token]: message };
      });
    },
  );

  const clearAvailabilityErrors = useCallback((tokens: string[]) => {
    setAvailabilityErrors((previous) => {
      if (!tokens.some((token) => previous[token])) return previous;

      const next = { ...previous };
      tokens.forEach((token) => delete next[token]);
      return next;
    });
  }, []);

  const subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
  const isTenantInTrial =
    subscriptionInfo?.AccessStatus === Enums.AccessStatus.Trial;

  useEffect(() => {
    cancelAll();
    setAvailabilityErrors({});

    if (!open) return;

    setRows(emptyRows());
    setResults(null);
    setSending(false);
    setAnySent(false);
    setAttempted(false);
    setPage(0);
    setOnlyProblems(false);
    setConfirmingDiscard(false);
  }, [open, cancelAll]);

  useEffect(() => {
    if (!open || roleOptions.length > 0) return;

    let cancelled = false;

    const loadRoles = async () => {
      const primaryRoles = await employeeService.getPrimaryRoles();
      if (cancelled) return;

      const mapped = (primaryRoles || [])
        .map((role: any) => {
          if (typeof role === "string") return { value: role, label: role };
          const value =
            role?.value ??
            role?.Value ??
            role?.Name ??
            role?.label ??
            role?.Label;
          const label = role?.label ?? role?.Label ?? role?.Name ?? value;
          return { value: value ?? "", label: label ?? "" };
        })
        .filter((option: any) => !!option.value && !isOtherRole(option));

      setRoleOptions(mapped);
    };

    loadRoles();

    return () => {
      cancelled = true;
    };
  }, [open, roleOptions.length]);

  const updateRow = useCallback(
    (key: string, field: keyof InviteRow, value: string) => {
      const availabilityField = availabilityFields[field as string];

      if (availabilityField) {
        const token = availabilityKey(key, availabilityField);
        cancelCheck(token);
        clearAvailabilityErrors([token]);
      }

      setRows((previous) =>
        previous.map((row) =>
          row.key === key ? { ...row, [field]: value, errors: {} } : row,
        ),
      );
    },
    [cancelCheck, clearAvailabilityErrors],
  );

  const addRow = useCallback(() => {
    setOnlyProblems(false);
    setPage(Math.ceil((rows.length + 1) / pageSize) - 1);
    setRows((previous) => [...previous, newRow()]);
  }, [rows.length]);

  const removeRow = useCallback(
    (key: string) => {
      const tokens = [
        availabilityKey(key, "EmailAddress"),
        availabilityKey(key, "MobileNumber"),
      ];
      tokens.forEach(cancelCheck);
      clearAvailabilityErrors(tokens);

      setRows((previous) =>
        previous.length > 1
          ? previous.filter((row) => row.key !== key)
          : previous,
      );
    },
    [cancelCheck, clearAvailabilityErrors],
  );

  const handleFieldBlur = useCallback(
    (row: InviteRow, field: AvailabilityField) => {
      if (isBlankRow(row)) return;

      const value = field === "EmailAddress" ? row.email : row.mobile;
      const token = availabilityKey(row.key, field);
      const { errors } = Helper.validateInputs([
        validationItemFor(field, value),
      ]);
      const formatError = (errors as any)[field];

      clearAvailabilityErrors([token]);

      if (formatError) {
        cancelCheck(token);
        setRows((previous) =>
          previous.map((entry) =>
            entry.key === row.key
              ? { ...entry, errors: { ...entry.errors, [field]: formatError } }
              : entry,
          ),
        );
        return;
      }

      setRows((previous) =>
        previous.map((entry) =>
          entry.key === row.key
            ? { ...entry, errors: { ...entry.errors, [field]: undefined } }
            : entry,
        ),
      );

      queueCheck(token, field, value);
    },
    [cancelCheck, clearAvailabilityErrors, queueCheck],
  );

  const filledCount = useMemo(
    () => rows.filter((row) => !isBlankRow(row)).length,
    [rows],
  );

  const problemIndexes = useMemo(
    () =>
      rows.reduce<number[]>((indexes, row, index) => {
        const hasProblem =
          hasRowErrors(row) ||
          !!availabilityErrors[availabilityKey(row.key, "EmailAddress")] ||
          !!availabilityErrors[availabilityKey(row.key, "MobileNumber")];

        return !isBlankRow(row) && hasProblem ? [...indexes, index] : indexes;
      }, []),
    [rows, availabilityErrors],
  );

  const showingProblemsOnly = onlyProblems && problemIndexes.length > 0;
  const listedIndexes = showingProblemsOnly
    ? problemIndexes
    : rows.map((_, index) => index);
  const pageCount = Math.max(1, Math.ceil(listedIndexes.length / pageSize));
  const currentPage = Math.min(Math.max(page, 0), pageCount - 1);
  const visibleIndexes = listedIndexes.slice(
    currentPage * pageSize,
    currentPage * pageSize + pageSize,
  );
  const showPager =
    listedIndexes.length > pageSize ||
    showingProblemsOnly ||
    (attempted && problemIndexes.length > 0 && rows.length > pageSize);
  const showProblemsToggle =
    attempted && problemIndexes.length > 0 && rows.length > pageSize;

  const validate = () => {
    const seenEmails: string[] = [];
    const seenMobiles: string[] = [];
    let allValid = true;

    const validated = rows.map((row) => {
      if (isBlankRow(row)) return { ...row, errors: {} };

      const { isValid, errors } = Helper.validateInputs([
        validationItemFor("EmailAddress", row.email),
        validationItemFor("MobileNumber", row.mobile),
        {
          key: "PrimaryRole",
          value: row.role,
          required: true,
          type: Enums.ControlType.Text,
        },
      ]);

      const rowErrors: any = { ...errors };

      const emailTaken =
        availabilityErrors[availabilityKey(row.key, "EmailAddress")];
      const mobileTaken =
        availabilityErrors[availabilityKey(row.key, "MobileNumber")];

      if (emailTaken)
        rowErrors.EmailAddress = rowErrors.EmailAddress || emailTaken;
      if (mobileTaken)
        rowErrors.MobileNumber = rowErrors.MobileNumber || mobileTaken;

      const normalisedEmail = row.email.trim().toLowerCase();

      if (normalisedEmail) {
        if (seenEmails.includes(normalisedEmail)) {
          rowErrors.EmailAddress = "Duplicate email";
        } else {
          seenEmails.push(normalisedEmail);
        }
      }

      const normalisedMobile = row.mobile.replace(/\D/g, "");

      if (normalisedMobile) {
        if (seenMobiles.includes(normalisedMobile)) {
          rowErrors.MobileNumber = "Duplicate mobile number";
        } else {
          seenMobiles.push(normalisedMobile);
        }
      }

      if (!isValid || rowErrors.EmailAddress || rowErrors.MobileNumber)
        allValid = false;

      return { ...row, errors: rowErrors };
    });

    setRows(validated);

    return { allValid, validated };
  };

  const sendInvites = async () => {
    const { allValid, validated } = validate();

    setAttempted(true);

    if (!allValid) {
      if (validated.length > pageSize) {
        setOnlyProblems(true);
        setPage(0);
      }

      showNotification({
        color: "yellow.7",
        message: "Please fix the highlighted rows.",
        autoClose: 4000,
      });
      return;
    }

    const toSend = validated.filter((row) => !isBlankRow(row));

    if (toSend.length === 0) return;

    setSending(true);

    const outcomes: InviteResult[] = [];

    for (const row of toSend) {
      const { firstName, lastName } = splitFullName(row.fullName);
      const email = row.email.trim();
      const mobile = row.mobile.trim();

      let response: any = null;

      try {
        response = await Fetch.post({
          url: "/Employee",
          params: {
            Employee: {
              FirstName: firstName,
              LastName: lastName,
              EmailAddress: email,
              MobileNumber: mobile,
              SendEmail: true,
              SendSMS: true,
              SendNotifications: true,
              IsSalesRepresentative: false,
              IsTechnician: false,
              IsActive: true,
              DisplayColor: "",
              IsAllReport: true,
              IsAllJobCardStatus: true,
              PrimaryRole: row.role,
            },
            PermissionList: [],
            User: {
              UserName: email,
              IsActive: true,
              SessionTimeOut: 0,
            },
          },
        });
      } catch (error: any) {
        response = { message: error?.message };
      }

      if (response?.ID) {
        Helper.mixpanelTrack(Constants.mixPanelEvents.createEmployee, {
          employeeID: response.ID,
        });
        outcomes.push({
          email,
          mobile,
          firstName,
          lastName,
          role: row.role,
          sent: true,
          inviteLink: response.InviteLink,
          whatsAppUrl: response.InviteWhatsAppUrl,
        });
      } else {
        outcomes.push({
          email,
          mobile,
          firstName,
          lastName,
          role: row.role,
          sent: false,
          error:
            response?.Message ||
            response?.message ||
            response?.serverMessage ||
            "Something went wrong",
        });
      }
    }

    setSending(false);
    setResults(outcomes);
    setAnySent(outcomes.some((outcome) => outcome.sent));
  };

  const closeAfterInvites = () => {
    if (anySent) onInvited?.();
    onClose();
  };

  const requestClose = () => {
    if (!results && !sending && filledCount > 0) {
      setConfirmingDiscard(true);
      return;
    }

    closeAfterInvites();
  };

  const copyLink = async (link?: string) => {
    if (!link) return;

    await navigator.clipboard.writeText(link);
    showNotification({ color: "green.7", message: "Copied", autoClose: 2000 });
  };

  const inviteMore = () => {
    cancelAll();
    setAvailabilityErrors({});
    setRows(emptyRows());
    setResults(null);
    setAttempted(false);
    setPage(0);
    setOnlyProblems(false);
    setConfirmingDiscard(false);
  };

  const sentResults = results ? results.filter((result) => result.sent) : [];
  const failedResults = results ? results.filter((result) => !result.sent) : [];
  const sentCount = sentResults.length;

  const displayName = (result: InviteResult) =>
    `${result.firstName} ${result.lastName}`.trim();

  const successTitle =
    sentCount === 0
      ? "Nothing sent"
      : sentCount === 1
        ? `${displayName(sentResults[0]) || sentResults[0].email} added`
        : `${sentCount} employees added`;

  const successSubtitle =
    sentCount === 0
      ? "Fix the problems below, then try again."
      : `${sentCount} ${sentCount === 1 ? "invite" : "invites"} sent. Links last 7 days.`;

  const pager = (
    <Group gap={"xs"} wrap={"nowrap"}>
      {showProblemsToggle && (
        <Anchor
          component={"button"}
          type={"button"}
          size={"sm"}
          onClick={() => {
            setOnlyProblems(!showingProblemsOnly);
            setPage(0);
          }}
        >
          {showingProblemsOnly
            ? "Show all rows"
            : `Show ${problemIndexes.length} ${problemIndexes.length === 1 ? "row" : "rows"} with problems`}
        </Anchor>
      )}
      <Text size={"sm"} c={"gray.7"}>
        {listedIndexes.length ? currentPage * pageSize + 1 : 0}–
        {Math.min(listedIndexes.length, currentPage * pageSize + pageSize)} of{" "}
        {listedIndexes.length}
      </Text>
      <ActionIcon
        variant={"subtle"}
        color={"gray.7"}
        aria-label={"Previous page"}
        disabled={currentPage === 0}
        onClick={() => setPage(currentPage - 1)}
      >
        <IconChevronLeft size={16} />
      </ActionIcon>
      <Text size={"sm"} c={"gray.7"}>
        Page {currentPage + 1} of {pageCount}
      </Text>
      <ActionIcon
        variant={"subtle"}
        color={"gray.7"}
        aria-label={"Next page"}
        disabled={currentPage >= pageCount - 1}
        onClick={() => setPage(currentPage + 1)}
      >
        <IconChevronRight size={16} />
      </ActionIcon>
    </Group>
  );

  const formBody = (
    <div>
      <Group
        gap={8}
        wrap={"nowrap"}
        align={"flex-start"}
        px={12}
        py={8}
        bg={"scBlue.0"}
        style={{ borderRadius: 4 }}
      >
        <IconInfoCircle
          size={16}
          style={{ flexShrink: 0, marginTop: 2 }}
          color={"var(--mantine-color-scBlue-6)"}
        />
        <Text size={"sm"} c={"gray.8"}>
          Everyone you invite can sign in to ServCraft. They choose their own
          password when they join, so you don&apos;t need to set one.
        </Text>
      </Group>

      <Box mt={"md"} style={{ overflowX: "auto" }}>
        <Table
          verticalSpacing={"xs"}
          withRowBorders={false}
          style={{ minWidth: 820, tableLayout: "fixed", width: "100%" }}
        >
          <colgroup>
            <col style={{ width: "20%" }} />
            <col style={{ width: "34%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "27%" }} />
            <col style={{ width: "4%" }} />
          </colgroup>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Full name</Table.Th>
              <Table.Th>Email{requiredMark}</Table.Th>
              <Table.Th>Mobile{requiredMark}</Table.Th>
              <Table.Th>Role{requiredMark}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {visibleIndexes.map((index) => {
              const row = rows[index];
              const isFirstRow = index === 0;

              return (
                <Table.Tr key={row.key}>
                  <Table.Td>
                    <TextInput
                      placeholder={isFirstRow ? "Full name" : undefined}
                      aria-label={`Full name row ${index + 1}`}
                      value={row.fullName}
                      onChange={(event) =>
                        updateRow(
                          row.key,
                          "fullName",
                          event.currentTarget.value,
                        )
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      type={"email"}
                      placeholder={
                        isFirstRow ? "name@company.co.za" : undefined
                      }
                      aria-label={`Email row ${index + 1}`}
                      value={row.email}
                      error={
                        row.errors.EmailAddress ||
                        availabilityErrors[
                          availabilityKey(row.key, "EmailAddress")
                        ]
                      }
                      onChange={(event) =>
                        updateRow(row.key, "email", event.currentTarget.value)
                      }
                      onBlur={() => handleFieldBlur(row, "EmailAddress")}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      type={"tel"}
                      placeholder={isFirstRow ? "082 555 0100" : undefined}
                      aria-label={`Mobile row ${index + 1}`}
                      value={row.mobile}
                      error={
                        row.errors.MobileNumber ||
                        availabilityErrors[
                          availabilityKey(row.key, "MobileNumber")
                        ]
                      }
                      onChange={(event) =>
                        updateRow(row.key, "mobile", event.currentTarget.value)
                      }
                      onBlur={() => handleFieldBlur(row, "MobileNumber")}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      data={roleOptions}
                      value={row.role || null}
                      error={row.errors.PrimaryRole}
                      placeholder={"Select"}
                      aria-label={`Role row ${index + 1}`}
                      allowDeselect={false}
                      comboboxProps={{ withinPortal: true }}
                      onChange={(value) =>
                        updateRow(row.key, "role", value || "")
                      }
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      variant={"subtle"}
                      color={"gray.7"}
                      aria-label={`Remove row ${index + 1}`}
                      disabled={rows.length <= 1}
                      onClick={() => removeRow(row.key)}
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Box>

      <Flex
        mt={"sm"}
        align={"center"}
        justify={"space-between"}
        gap={"sm"}
        wrap={"wrap"}
      >
        <Anchor component={"button"} type={"button"} onClick={addRow}>
          <Group gap={6} wrap={"nowrap"}>
            <IconPlus size={16} />
            <span>Add another person</span>
          </Group>
        </Anchor>
        {showPager && pager}
      </Flex>
    </div>
  );

  const personRow = (result: InviteResult, withLinks: boolean) => (
    <Paper
      key={result.email}
      withBorder
      radius={"sm"}
      px={"md"}
      py={"sm"}
      mb={"xs"}
    >
      <Flex align={"center"} justify={"space-between"} gap={"md"} wrap={"wrap"}>
        <div style={{ minWidth: 180, flexGrow: 1 }}>
          <Text size={"md"} fw={600} c={"gray.9"}>
            {displayName(result) || result.email}
          </Text>
          {withLinks ? (
            <Text size={"sm"} c={"gray.6"}>
              Invite sent by email and SMS · {result.role}
            </Text>
          ) : (
            <Text size={"sm"} c={"red.7"}>
              {result.error}
            </Text>
          )}
        </div>
        {withLinks ? (
          <Group gap={"xs"} wrap={"nowrap"}>
            <Button
              variant={"outline"}
              color={"scBlue"}
              leftSection={<IconLink size={16} />}
              disabled={!result.inviteLink}
              onClick={() => copyLink(result.inviteLink)}
            >
              Copy link
            </Button>
            <Button
              variant={"outline"}
              color={"scBlue"}
              leftSection={<IconBrandWhatsapp size={16} color={"#25D366"} />}
              component={"a"}
              href={result.whatsAppUrl}
              target={"_blank"}
              rel={"noreferrer"}
              disabled={!result.whatsAppUrl}
            >
              WhatsApp
            </Button>
          </Group>
        ) : (
          <Badge color={"red"} variant={"light"}>
            Not sent
          </Badge>
        )}
      </Flex>
    </Paper>
  );

  const successBody = (
    <div>
      {sentCount > 0 && (
        <Text size={"md"} c={"gray.8"}>
          If someone doesn&apos;t see their invite, share their link yourself.
        </Text>
      )}

      <Box mt={sentCount > 0 ? "md" : 0}>
        {sentResults.map((result) => personRow(result, true))}
      </Box>

      {failedResults.length > 0 && (
        <Box mt={"md"}>
          <Text size={"sm"} c={"gray.7"} mb={"xs"}>
            These were not sent. Invite them again once the problem is fixed.
          </Text>
          {failedResults.map((result) => personRow(result, false))}
        </Box>
      )}
    </div>
  );

  const summary =
    filledCount === 0 ? (
      <div />
    ) : attempted && problemIndexes.length > 0 ? (
      <div>
        <Text size={"sm"} fw={700} c={"red.7"}>
          Fix {problemIndexes.length}{" "}
          {problemIndexes.length === 1 ? "row" : "rows"} to continue.
        </Text>
        <Text size={"xs"} c={"gray.6"}>
          Problems are highlighted in the list.
        </Text>
      </div>
    ) : (
      <div>
        <Text size={"sm"} fw={700} c={"gray.9"}>
          {peopleLabel(filledCount)}
        </Text>
        {!isTenantInTrial && (
          <Text size={"xs"} c={"gray.6"}>
            {filledCount} {filledCount === 1 ? "licence" : "licences"} will be
            added to your subscription.
          </Text>
        )}
      </div>
    );

  const discardFooter = (
    <Flex
      w={"100%"}
      align={"center"}
      justify={"space-between"}
      gap={"sm"}
      wrap={"wrap"}
    >
      <Text size={"sm"} c={"gray.7"}>
        <Text component={"span"} size={"sm"} fw={700} c={"gray.9"}>
          Discard {peopleLabel(filledCount)}?
        </Text>{" "}
        Nothing has been saved yet.
      </Text>
      <Group gap={"xs"}>
        <Button
          variant={"outline"}
          color={"gray.7"}
          onClick={() => setConfirmingDiscard(false)}
        >
          Keep editing
        </Button>
        <Button color={"red"} onClick={closeAfterInvites}>
          Discard
        </Button>
      </Group>
    </Flex>
  );

  const formFooter = (
    <Flex
      w={"100%"}
      align={"center"}
      justify={"space-between"}
      gap={"sm"}
      wrap={"wrap"}
    >
      {summary}
      <Group gap={"xs"}>
        <Button
          variant={"outline"}
          color={"gray.7"}
          disabled={sending}
          onClick={requestClose}
        >
          {origin === "onboarding" ? "Skip for now" : "Cancel"}
        </Button>
        <Button
          color={"scBlue"}
          loading={sending}
          disabled={sending || filledCount === 0}
          onClick={sendInvites}
        >
          {filledCount > 1 ? `Send ${filledCount} invites` : "Send invite"}
        </Button>
      </Group>
    </Flex>
  );

  const successFooter = (
    <Flex
      w={"100%"}
      align={"center"}
      justify={"space-between"}
      gap={"sm"}
      wrap={"wrap"}
    >
      <Text size={"md"} c={"gray.7"}>
        You&apos;ll find everyone in Settings › Employees.
      </Text>
      <Group gap={"sm"}>
        <Button variant={"default"} onClick={inviteMore}>
          Add more people
        </Button>
        <Button color={"scBlue"} onClick={closeAfterInvites}>
          Done
        </Button>
      </Group>
    </Flex>
  );

  const copy = headerCopy[origin];

  return (
    <SCModal
      open={open}
      onClose={requestClose}
      size={results ? 760 : 1080}
      autoHeight
      withCloseButton
      modalProps={{ closeOnClickOutside: false }}
      headerSection={
        <Box px={"lg"} pt={"lg"} pr={48}>
          {results ? (
            <Flex gap={"sm"} align={"flex-start"}>
              {sentCount > 0 && (
                <Box
                  style={{
                    background: "var(--mantine-color-green-0)",
                    borderRadius: "50%",
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color: "var(--mantine-color-green-7)",
                  }}
                >
                  <IconCheck size={20} stroke={2.5} />
                </Box>
              )}
              <div>
                <Text size={"24px"} lh={1.2} fw={700} c={"gray.9"}>
                  {successTitle}
                </Text>
                <Text size={"md"} c={"gray.7"} mt={6}>
                  {successSubtitle}
                </Text>
              </div>
            </Flex>
          ) : (
            <>
              <Text size={"xl"} fw={700} c={"gray.9"}>
                {copy.title}
              </Text>
              <Text size={"sm"} c={"gray.6"} mt={4}>
                {copy.subtitle}
              </Text>
            </>
          )}
        </Box>
      }
      footerSection={
        results ? successFooter : confirmingDiscard ? discardFooter : formFooter
      }
    >
      {results ? successBody : formBody}
    </SCModal>
  );
};

export default InviteEmployeesModal;
